import { useEffect, useRef, useState, type ReactNode, type TouchEvent } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { X } from 'lucide-react';
import { Button } from './Button';
import { Field, Textarea } from './Form';

/** Stapel offener Overlays: Escape schließt immer nur das oberste (z. B. Dialog über Drawer). */
const overlayStack: symbol[] = [];

function useEscape(open: boolean, onClose: () => void) {
  // onClose über Ref, damit Neu-Renderings die Stapelreihenfolge nicht verändern
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const id = Symbol('overlay');
    overlayStack.push(id);
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && overlayStack[overlayStack.length - 1] === id) {
        e.stopPropagation();
        closeRef.current();
      }
    };
    document.addEventListener('keydown', h);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', h);
      const idx = overlayStack.indexOf(id);
      if (idx >= 0) overlayStack.splice(idx, 1);
      document.body.style.overflow = prev;
    };
  }, [open]);
}

/** Bottom-Sheet am Smartphone: am Griff/Kopf nach unten ziehen schließt den Dialog */
function useSwipeToClose(onClose: () => void) {
  const panel = useRef<HTMLDivElement>(null);
  const start = useRef<number | null>(null);
  const delta = useRef(0);
  const set = (y: number, animate: boolean) => {
    const el = panel.current;
    if (!el) return;
    el.style.transition = animate ? 'transform 0.2s ease-out' : 'none';
    el.style.transform = y > 0 ? `translateY(${y}px)` : '';
  };
  return {
    panel,
    handlers: {
      onTouchStart: (e: TouchEvent) => {
        if (window.matchMedia('(min-width: 640px)').matches) return;
        start.current = e.touches[0]!.clientY;
        delta.current = 0;
      },
      onTouchMove: (e: TouchEvent) => {
        if (start.current === null) return;
        delta.current = Math.max(0, e.touches[0]!.clientY - start.current);
        set(delta.current, false);
      },
      onTouchEnd: () => {
        if (start.current === null) return;
        start.current = null;
        if (delta.current > 110) onClose();
        else set(0, true);
      },
    },
  };
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  useEscape(open, onClose);
  const swipe = useSwipeToClose(onClose);
  if (!open) return null;
  const width = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-3xl', xl: 'max-w-5xl' }[size];
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4" role="dialog" aria-modal="true">
      <div className="anim-fade absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div ref={swipe.panel} className={clsx('anim-sheet relative flex max-h-[92dvh] w-full flex-col rounded-t-2xl bg-white shadow-xl sm:rounded-xl', width)}>
        <div {...swipe.handlers} className="touch-none sm:touch-auto">
          <div className="mx-auto mt-2 h-1.5 w-10 rounded-full bg-slate-300 sm:hidden" aria-hidden />
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
            <h3 className="text-base font-semibold text-slate-900">{title}</h3>
            <button type="button" onClick={onClose} className="-mr-1 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Schließen">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4">{children}</div>
        {footer && (
          <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 max-sm:[&>*]:flex-1">{footer}</div>
        )}
        {!footer && <div className="pb-safe sm:hidden" />}
      </div>
    </div>,
    document.body,
  );
}

export function Drawer({
  open,
  onClose,
  title,
  children,
  footer,
  width = 'max-w-xl',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
}) {
  useEscape(open, onClose);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <div className="anim-fade absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <div className={clsx('anim-drawer relative flex h-full w-full flex-col bg-white pt-safe shadow-xl', width)}>
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="min-w-0 truncate text-base font-semibold text-slate-900">{title}</h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Bestätigungsdialog. Mit requireReason wird ein Pflicht-Begründungsfeld angezeigt
 * (Storno, Umbuchung, Verwerfen – Finanzdaten werden nie ohne Begründung geändert).
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Bestätigen',
  tone = 'primary',
  requireReason,
  reasonLabel = 'Begründung',
  loading,
  children,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void | Promise<void>;
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  tone?: 'primary' | 'danger' | 'warning';
  requireReason?: boolean;
  reasonLabel?: string;
  loading?: boolean;
  children?: ReactNode;
}) {
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  const invalid = requireReason && reason.trim().length < 3;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button variant={tone} loading={loading} disabled={invalid} onClick={() => onConfirm(reason.trim())}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {message && <div className="text-sm text-slate-600">{message}</div>}
      {children}
      {requireReason && (
        <Field label={reasonLabel} required className="mt-4" hint="Wird im Audit-Log gespeichert.">
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
        </Field>
      )}
    </Modal>
  );
}

export function Tabs<T extends string>({ tabs, active, onChange }: { tabs: { key: T; label: ReactNode; count?: number }[]; active: T; onChange: (k: T) => void }) {
  return (
    <div className="border-b border-slate-200">
      <nav className="-mb-px flex gap-1 overflow-x-auto px-2" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={active === t.key}
            onClick={() => onChange(t.key)}
            className={clsx(
              'flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium',
              active === t.key ? 'border-brand-700 text-brand-700' : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700',
            )}
          >
            {t.label}
            {t.count !== undefined && (
              <span className={clsx('rounded-full px-1.5 py-0.5 text-xs tabular', active === t.key ? 'bg-brand-100 text-brand-800' : 'bg-slate-100 text-slate-600')}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </nav>
    </div>
  );
}
